import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

let cartQueue = Promise.resolve();
let externalCartUpdate = Promise.resolve();

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.target instanceof Element && event.target.matches('p01-product-card')) return;
  if (event.promise) externalCartUpdate = event.promise.catch(() => undefined);
});

class P01ProductCard extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.data = JSON.parse(this.querySelector('[data-p01-product-data]').textContent);
    this.variantInput = this.querySelector('[data-p01-variant]');
    this.quantityInput = this.querySelector('[data-p01-quantity]');
    this.submitButton = this.querySelector('[data-p01-submit]');
    this.status = this.querySelector('[data-p01-status]');
    this.form.addEventListener('submit', (event) => this.submit(event), { signal });
    this.variantInput.addEventListener('change', () => this.updateVariant(true), { signal });
    this.quantityInput.addEventListener('input', () => this.quantityInput.setCustomValidity(''), { signal });
    this.updateVariant(false);
  }

  disconnectedCallback() {
    this.abort?.abort();
    clearTimeout(this.successTimer);
  }

  variant() {
    return this.data.variants.find((variant) => variant.id === this.variantInput.value);
  }

  updateVariant(resetQuantity) {
    if (resetQuantity) { clearTimeout(this.successTimer); this.added = false; }
    const variant = this.variant();
    if (!variant) {
      this.submitButton.disabled = true;
      this.submitButton.textContent = this.data.labels.choose;
      return;
    }
    this.querySelector('[data-p01-price]').textContent = variant.price;
    this.querySelector('[data-p01-compare]').hidden = !variant.compare;
    this.querySelector('[data-p01-compare-value]').textContent = variant.compare || '';
    this.quantityInput.min = String(variant.min);
    this.quantityInput.step = String(variant.increment);
    if (variant.max !== null) this.quantityInput.max = String(variant.max);
    else this.quantityInput.removeAttribute('max');
    this.quantityInput.setCustomValidity('');
    if (resetQuantity) this.quantityInput.value = String(variant.min);
    const rules = this.querySelector('[data-p01-rules]');
    const ruleTexts = [this.data.labels.minimum.replace('[quantity]', variant.min), this.data.labels.increment.replace('[quantity]', variant.increment)];
    if (variant.max !== null) ruleTexts.push(this.data.labels.maximum.replace('[quantity]', variant.max));
    rules.textContent = ruleTexts.join(' · ');
    rules.hidden = variant.min === 1 && variant.increment === 1 && variant.max === null;
    if (!this.pending && !this.uncertain) {
      this.submitButton.disabled = !variant.available;
      this.submitButton.textContent = variant.available ? this.data.labels.add : this.data.labels.soldOut;
      if (resetQuantity) this.setStatus('');
    }
  }

  setStatus(message, tone = '') {
    this.status.textContent = message;
    this.status.dataset.tone = tone;
  }

  setBusy(busy) {
    this.pending = busy;
    this.form.setAttribute('aria-busy', String(busy));
    this.submitButton.disabled = busy || this.uncertain || !this.variant()?.available;
    if (this.variantInput instanceof HTMLSelectElement) this.variantInput.disabled = busy;
    this.quantityInput.readOnly = busy;
    if (!busy) {
      this.submitButton.textContent = this.added ? this.data.labels.added : this.variant()?.available ? this.data.labels.add : this.data.labels.soldOut;
      if (this.added) this.successTimer = setTimeout(() => {
        this.added = false;
        if (this.isConnected && !this.pending) this.updateVariant(false);
      }, 1800);
    }
  }

  submit(event) {
    event.preventDefault();
    if (this.pending || this.uncertain) return;
    const variant = this.variant();
    const quantity = Number(this.quantityInput.value);
    if (!variant?.available) return;
    if (!Number.isSafeInteger(quantity) || quantity < variant.min || (quantity - variant.min) % variant.increment !== 0 || (variant.max !== null && quantity > variant.max)) {
      this.quantityInput.setCustomValidity(this.data.labels.quantityError);
      this.quantityInput.reportValidity();
      return;
    }
    if (!this.form.reportValidity()) return;
    const formData = new FormData(this.form);
    formData.set('id', variant.id);
    formData.set('quantity', String(quantity));
    clearTimeout(this.successTimer);
    this.added = false;
    this.setBusy(true);
    this.submitButton.textContent = this.data.labels.adding;
    this.setStatus(this.data.labels.queued);
    cartQueue = cartQueue.catch(() => undefined).then(async () => {
      await externalCartUpdate;
      if (!this.isConnected) return;
      await this.add(formData, variant, quantity);
    }).finally(() => this.setBusy(false));
  }

  async readCart() {
    const response = await fetch(`${this.data.cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(20000) });
    if (!response.ok) throw new Error(this.data.labels.refreshError);
    return response.json();
  }

  publishCart(cart, sections, variant, quantity, didError) {
    const result = {
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, sections, itemCount: cart.item_count, source: 'p01-product-card', sourceId: this.id, productId: this.data.productId, didError }
    };
    const target = this.isConnected ? this : document;
    target.dispatchEvent(new CartLinesUpdateEvent({ action: didError ? 'update' : 'add', context: 'product', lines: [{ merchandiseId: variant.id, quantity }], promise: Promise.resolve(result) }));
  }

  async add(formData, variant, quantity) {
    let confirmed = false;
    let requestSent = false;
    this.setStatus(this.data.labels.adding);
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((element) => element.dataset.sectionId))].slice(0, 5);
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));
    formData.set('sections_url', window.location.pathname);
    try {
      if (variant.max !== null) {
        const before = await this.readCart();
        const existing = before.items.filter((item) => String(item.variant_id) === variant.id).reduce((total, item) => total + item.quantity, 0);
        if (existing + quantity > variant.max) {
          this.setStatus(this.data.labels.maximumError, 'error');
          return;
        }
      }
      requestSent = true;
      const response = await fetch(this.data.addUrl, { method: 'POST', body: formData, headers: { Accept: 'application/json' }, credentials: 'same-origin', signal: AbortSignal.timeout(20000) });
      const data = await response.json();
      if (!response.ok || data.status) {
        if (response.status >= 500) throw new Error(this.data.labels.unknown);
        const message = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : this.data.labels.error;
        this.setStatus(message, 'error');
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        try {
          const cart = await this.readCart();
          this.publishCart(cart, data.sections, variant, quantity, true);
        } catch {
          this.querySelector('[data-p01-check-cart]').hidden = false;
        }
        return;
      }
      confirmed = true;
      this.added = true;
      this.submitButton.textContent = this.data.labels.added;
      const cart = await this.readCart();
      this.publishCart(cart, data.sections, variant, quantity, false);
      this.setStatus(this.data.labels.added);
    } catch {
      if (requestSent) {
        this.uncertain = true;
        this.querySelector('[data-p01-check-cart]').hidden = false;
        this.setStatus(confirmed ? this.data.labels.refreshError : this.data.labels.unknown, 'error');
      } else {
        this.setStatus(this.data.labels.error, 'error');
      }
    }
  }
}

if (!customElements.get('p01-product-card')) customElements.define('p01-product-card', P01ProductCard);
